import React, { useEffect, useCallback, useState } from 'react'
import {
  Paragraph,
  Button,
  Checkbox,
  DialogOverlay,
  DialogOverlayActions,
  List,
  ListItem,
  Separator,
  colors,
  useOverlay,
} from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { useWatch, useZionFormContext } from '@fs/zion-form'
import { css } from '@emotion/core'
import { useQueryParams, useLocation } from '@fs/zion-router'
import { useGroupSearch, useRouterHelper } from 'hooks'
import { createSearchCriteria } from 'helpers'
import { SNAPSHOT_LOCAL_STORAGE_KEY } from '../utilities'
import { getCoverageDatesFromGedcomX } from '../../../helpers/dateUtils'

const greyBox = css`
  background-color: ${colors.gray02};
  padding: 0px 8px;
`
const bumpIn = css`
  padding-inline-start: 2px;
`

const transl = {
  title: () => i18n.t('snapshotInTime.title', "There's an issue with the place name"),
  body: () =>
    i18n.t(
      'snapshotInTime.body',
      'The names of places often change over time, and the name you entered might not be the best match for the date range. For a better search, try this:'
    ),
  suggestedPlace: () => i18n.t('snapshotInTime.suggested', 'Suggested Place'),
  useThisPlacePrompt: () => i18n.t('snapshotInTime.useThisPlacePrompt', 'Would you like to use this suggested place?'),
  doNotShowAgain: () => i18n.t('snapshotInTime.doNotShowPrompt', 'Do not show this again for 1 hour'),
}

export default function SnapshotInTimeDialog({ suggestion = null }) {
  const overlay = useOverlay()
  const { setGroupSearchQueryParams } = useGroupSearch()
  const { query } = useQueryParams()
  const location = useLocation()
  const place = useWatch({ name: 'place' })
  const date = useWatch({ name: 'date' })
  const { navigate } = useRouterHelper()

  const [doNotShowAgain, setDoNotShowAgain] = useState(false)
  const [heldPlace, setHeldPlace] = useState(null)
  const { getValues } = useZionFormContext()

  // Due to how the results pages works, the place gets reset to the query string value when the dialog opens.
  // To avoid complicating the results page, I've written this useEffect and useState combo to store any place that is standardized.
  useEffect(() => {
    if (!heldPlace && place) setHeldPlace(place)
    else if (heldPlace?.key !== place?.key && !!heldPlace && !!place && place?.standard) {
      setHeldPlace(place)
    }
  }, [heldPlace, place, query])

  const updateSearch = useCallback(
    (newPlace, newDate) => {
      const [startDate, endDate] = getCoverageDatesFromGedcomX(newDate.standard.formalText)
      const safeCriteria = createSearchCriteria({ ...getValues(), place: newPlace, startDate, endDate })

      if (!location.pathname.includes('search-results')) {
        navigate({
          path: 'records/images/search-results',
          query: safeCriteria,
          replaceState: true,
        })
      } else
        setGroupSearchQueryParams({
          ...safeCriteria,
          place: newPlace,
          startDate,
          endDate,
        })
    },
    [getValues, location, navigate, setGroupSearchQueryParams]
  )

  const closeDialog = useCallback(() => {
    if (doNotShowAgain) {
      const NOW = new Date()
      localStorage[SNAPSHOT_LOCAL_STORAGE_KEY] = JSON.stringify({
        expires: NOW.getTime() + 1 * 60 * 60 * 1000,
      })
    }

    overlay.handleClick()
  }, [doNotShowAgain, overlay])

  useEffect(() => {
    let doNotShowCheck = false
    try {
      const doNotShowOBJ = JSON.parse(localStorage[SNAPSHOT_LOCAL_STORAGE_KEY])
      const NOW = new Date()
      if (NOW < doNotShowOBJ.expires) doNotShowCheck = true
      else localStorage.removeItem(SNAPSHOT_LOCAL_STORAGE_KEY)
    } catch {
      doNotShowCheck = false
    }
    if (suggestion?.name && !overlay.isOpen && !doNotShowCheck) overlay.handleClick()
    // only update the display of the dialog based on the suggestion, not on the overlay
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestion])

  const setSuggestedPlace = useCallback(() => {
    updateSearch(suggestion.id, date)
    closeDialog()
  }, [updateSearch, suggestion, date, closeDialog])

  const closeDialogAndUpdateSearch = useCallback(() => {
    updateSearch(heldPlace?.key, date)
    closeDialog()
  }, [closeDialog, date, heldPlace, updateSearch])

  return (
    <DialogOverlay {...overlay} title={transl.title()} nonClosable>
      <Paragraph size="sm">{transl.body()}</Paragraph>
      <div css={greyBox}>
        <List>
          <ListItem
            overline={transl.suggestedPlace()}
            primaryText={suggestion.name ?? ''}
            secondaryText={`${suggestion.fromYear}-${suggestion.toYear}`}
          />
        </List>
      </div>
      <Separator size="nano" />
      <Paragraph size="sm">{transl.useThisPlacePrompt()}</Paragraph>
      <Separator size="xs" />
      <div css={bumpIn}>
        <Checkbox
          label={transl.doNotShowAgain()}
          value={doNotShowAgain}
          checked={doNotShowAgain}
          onChange={() => setDoNotShowAgain(!doNotShowAgain)}
          name="do-not-show-this-dialog-again"
        />
      </div>
      <Separator size="md" />
      <DialogOverlayActions>
        <Button onClick={closeDialogAndUpdateSearch}>{i18n.t('common-ui:no.action')}</Button>
        <Button emphasis="high" onClick={setSuggestedPlace}>
          {i18n.t('common-ui:yes.action')}
        </Button>
      </DialogOverlayActions>
    </DialogOverlay>
  )
}
